import { createFileRoute } from "@tanstack/react-router";
import axios from "axios";
import { useEffect, useState } from "react";
import { DeviceNotAllowed } from "@/components/test-execution/device-not-allowed";
import { ExamContent } from "@/components/test-execution/exam-content";
import { ExamError } from "@/components/test-execution/exam-error";
import { ExamFinishedRedirect } from "@/components/test-execution/exam-finished-redirect";
import { ExamForbidden } from "@/components/test-execution/exam-forbidden";
import { ExamHeader } from "@/components/test-execution/exam-header";
import { ExamLoading } from "@/components/test-execution/exam-loading";
import { LeaveTabDetection } from "@/components/test-execution/leave-tab-detection";
import { QuestionNavigator } from "@/components/test-execution/question-navigator";
import { StartPage } from "@/components/test-execution/start-page";
import { TokenGate } from "@/components/test-execution/token-gate";
import { useApplicationIntro } from "@/hooks/queries/use-application-intro";
import { useDeviceAllowed } from "@/hooks/use-device-allowed";
import { useExamSecurity } from "@/hooks/use-exam-security";
import { AccessibilityProvider } from "@/providers/accessibility-provider";

export const Route = createFileRoute("/provas/$id")({
	component: ExamPage,
});

function ExamPage() {
	const { id } = Route.useParams();
	return (
		<AccessibilityProvider>
			<ExamStateRouter applicationId={id} />
		</AccessibilityProvider>
	);
}

function ExamStateRouter({ applicationId }: { applicationId: string }) {
	const [tokenVerifiedThisLoad, setTokenVerifiedThisLoad] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: queremos resetar verificação ao trocar de prova
	useEffect(() => {
		setTokenVerifiedThisLoad(false);
	}, [applicationId]);

	const { data: intro, isLoading, error } = useApplicationIntro(applicationId);

	const { allowed, deviceLabel, allowedDevices } = useDeviceAllowed(
		intro?.application,
	);

	if (isLoading) return <ExamLoading />;

	if (error) {
		const status = axios.isAxiosError(error)
			? error.response?.status
			: undefined;
		return <ExamError status={status} />;
	}

	if (!intro) return <ExamError status={404} />;
	if (intro.forbidden)
		return <ExamForbidden category={intro.application.category} />;
	if (intro.finishedIn)
		return <ExamFinishedRedirect applicationId={applicationId} />;

	if (!allowed)
		return (
			<DeviceNotAllowed
				deviceLabel={deviceLabel}
				allowedDevices={allowedDevices}
			/>
		);

	if (intro.application.hasToken && !tokenVerifiedThisLoad) {
		return <TokenGate onTokenVerified={() => setTokenVerifiedThisLoad(true)} />;
	}

	if (!intro.startedIn) return <StartPage />;

	return <ExamLayout />;
}

function ExamLayout() {
	useExamSecurity(true);

	return (
		<div className="flex flex-col min-h-screen bg-white hc:bg-[#020617] select-none">
			<LeaveTabDetection />
			<ExamHeader />
			<div className="relative flex w-full flex-auto">
				<QuestionNavigator />
				<ExamContent />
			</div>
		</div>
	);
}
